/**
 * useCelebration — Manages goal completion celebration animations (confetti, sounds).
 */
import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { logger } from '@/utils/logger';

interface CelebrationData {
  goalTitle: string;
  pointsEarned: number;
  goalId?: string;
}

interface CelebrationContextType {
  isOpen: boolean;
  celebrationData: CelebrationData | null;
  triggerCelebration: (goalTitle: string, pointsEarned: number, goalId?: string) => void;
  closeCelebration: () => void;
}

const CelebrationContext = createContext<CelebrationContextType | undefined>(undefined);

export function CelebrationProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [celebrationData, setCelebrationData] = useState<CelebrationData | null>(null);

  const triggerCelebration = useCallback((goalTitle: string, pointsEarned: number, goalId?: string) => {
    logger.debug(`[CelebrationProvider] 🎉 Triggering celebration for "${goalTitle}"`);
    setCelebrationData({ goalTitle, pointsEarned, goalId });
    setIsOpen(true);
  }, []);

  const closeCelebration = useCallback(() => {
    logger.debug(`[CelebrationProvider] ✖ Closing celebration modal`);
    setIsOpen(false);
    // Keep data briefly for exit animation
    setTimeout(() => setCelebrationData(null), 300);
  }, []);

  return (
    <CelebrationContext.Provider value={{ isOpen, celebrationData, triggerCelebration, closeCelebration }}>
      {children}
    </CelebrationContext.Provider>
  );
}

export function useCelebration() {
  const context = useContext(CelebrationContext);
  if (context === undefined) {
    throw new Error("useCelebration must be used within a CelebrationProvider");
  }
  return context;
}
